import styled, { css } from 'styled-components';
import { WithDirection } from '@jjj/components';
import { palette } from 'styled-theme';
import { Link } from 'react-router-dom';

const CellsWrapper = styled.div`
  width: ${props => props.$width || '100%'};
  text-align: ${props => props.$align || 'left'};
  direction: ${props => props.$direction || 'inherit'};

  ${props =>
    props.$align &&
    css`
      > .text-cell-text-wrapper,
      > a.link-to-new-page {
        text-align: ${props.$align} !important;
      }
    `}

  .hover-header {
    display: none;
    font-size: 10px;
    color: ${palette('grayscale', 1)};
    transition: opacity 0.5s;
    letter-spacing: 0.25px;
  }

  .link-to-new-page {
    &:hover,
    &:focus {
      text-decoration: none;
    }
  }

  &.with-hover {
    /* Visually hidden at rest rather than display:none, so the text stays in the accessibility tree; a cell
       without hover text keeps its empty heading out of it. */
    .hover-header {
      display: block;
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
    }

    &:hover,
    &:focus-within {
      a.link-to-new-page,
      div.text-cell-text-wrapper {
        padding-top: 0 !important;
        padding-bottom: ${props => (props.withHover ? '8px' : '15px')} !important;
      }

      /* The header sits above the link and carries the top and leading inset the link used to give it. */
      .hover-header {
        position: static;
        width: auto;
        height: auto;
        overflow: visible;
        clip: auto;
        white-space: normal;
        padding: 7px 0 0;
        padding-inline-start: 7px;
      }
    }
  }

  .info-icon {
    color: ${palette('info', 0)};
  }

  .custom-cell-icon-wrapper {
    align-items: center;
    display: flex;
    font-weight: normal;
    min-height: 21px;
    padding-left: ${props => (props['data-rtl'] === 'rtl' ? 'auto' : '7px')};
    padding-right: ${props => (props['data-rtl'] === 'rtl' ? '7px' : 'auto')};

    h5 {
      margin-bottom: 0;
    }
  }

  .custom-cell-icon {
    align-content: center;
    border-radius: 50%;
    color: white;
    display: flex;
    justify-content: center;

    &-tooltip-content {
      display: flex;
      align-items: center;
      pointer-events: all;
    }
  }

  .has-error .ant-legacy-form-explain {
    font-weight: normal;
    white-space: pre-wrap;
  }

  .custom-cell-icon svg {
    height: auto !important;
    width: 14px !important;
  }

  .custom-cell-icon-text {
    color: ${palette('grayscale', 0)};
    line-height: 1;

    &-left {
      margin-left: 5px;
    }

    &-right {
      margin-right: 5px;
    }
  }

  .inactive-cell {
    color: ${palette('grayscale', 1)};
  }

  .hover-header,
  p {
    margin-bottom: 0;
  }

  p {
    letter-spacing: 0.35px;
    line-height: normal;
  }

  .input-form-item-cell {
    width: 160px;

    .ant-legacy-form-item {
      margin: 0;

      &.ant-legacy-form-item-with-help {
        margin: 8px 0;
      }

      .has-error .ant-legacy-form-explain {
        font-size: 12px;
        margin-top: 3px;
        line-height: normal;
      }
    }
  }

  .text-cell-with-tag {
    display: flex;
    align-items: center;

    .tag {
      line-height: 1.4;
      font-size: 9px;
      padding: 2px 7px;
      border-radius: 10px;
      background-color: ${palette('secondary', 0)};
      color: white;
    }
  }

  .hover-more-tooltip-wrapper {
    display: flex;
    align-items: center;
  }
`;

export const TooltipItem = styled.span`
  display: flex;
  justify-content: center;
`;

export const TooltipItemHeaderLabel = styled.span`
  display: flex;
  justify-content: center;
  font-weight: 600;
  margin-bottom: 4px;
`;

export const TruncatedItems = styled.span`
  &:after {
    content: '...';
    display: ${props => (props.showTruncatedChar ? 'inline-block' : 'none')};
  }
`;

const ItemsCountStyles = styled.span`
  font-size: 10px;
  cursor: default;
  background-color: ${palette('primary', 0)};
  color: ${palette('grayscale', 5)};
  border-radius: 50%;
  margin-left: ${props => (props['data-rtl'] === 'rtl' ? '0' : '4px')};
  margin-right: ${props => (props['data-rtl'] === 'rtl' ? '4px' : '0')};
  width: 17px;
  height: 17px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`;

const ItemsCount = WithDirection(ItemsCountStyles);

export { ItemsCount };

export default WithDirection(CellsWrapper);

export const PopoverIconWrapper = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  height: 100%;
  justify-content: center;
  line-height: 20px;
  width: 56px;
`;

export const PopoverWrapper = styled.div`
  height: ${props => props.$height || 51}px;
  width: 56px;

  .ant-popover-inner-content {
    padding: 11px 0px 9px;
  }
`;

const DropdownWrapperStyles = styled.div`
  .popover-buttons-container {
    min-width: 120px;

    button,
    a {
      background-color: unset;
      border: none;
      color: ${palette('secondary', 0)} !important;
      cursor: pointer;
      display: flex !important;
      font-family: Helvetica;
      font-size: 14px;
      padding: 4px 0 4px 0 !important;
      position: relative;
      text-align: ${props => (props['data-rtl'] === 'rtl' ? 'right' : 'left')};
      white-space: nowrap;
      width: 100% !important;
      text-transform: capitalize;

      &:hover {
        background-color: ${palette('background', 0)};
        color: ${palette('secondary', 0)};
        cursor: pointer;
      }

      &.danger .actionName {
        color: ${palette('error', 0)};
      }

      &:disabled {
        background-color: transparent;
        cursor: not-allowed;

        .actionName {
          color: ${palette('grayscale', 1)};
        }
      }
    }
  }
`;

export const LockedIcon = styled('i')`
  color: ${palette('primary', 0)};
  margin-left: ${props => (props['data-rtl'] === 'rtl' ? '0' : '5px')};
  margin-right: ${props => (props['data-rtl'] === 'rtl' ? '5px' : '0')};
  align-items: center;
  display: flex;

  svg {
    width: 16px;
    height: 16px;

    path {
      fill: ${palette('grayscale', 1)};
    }
  }
`;

const TapToCopyCellWrapperStyles = styled('div')`
  .copy-text-container {
    cursor: pointer;
  }

  .copy-text-icon {
    color: ${palette('secondary', 0)};
    cursor: pointer;
    font-size: 12px;
    padding-left: ${props => (props['data-rtl'] === 'rtl' ? 'auto' : '4px')};
    padding-right: ${props => (props['data-rtl'] === 'rtl' ? '4px' : 'auto')};
  }

  .copy-text-tooltip {
    align-items: center;
    display: inline-flex;
  }
`;

// Disabled is grey, not secondary[1] (#ADF1FF) — ~1.2:1 on a white row, and pointer-events:none leaves no hover cue.
export const ExternalLink = styled.a`
  color: ${props => (props.$disabled ? palette('grayscale', 1) : palette('secondary', 0))} !important;
  text-decoration: underline;
  pointer-events: ${props => (props.$disabled ? 'none' : 'auto')};
`;

export const StyledLink = styled(Link)`
  color: ${palette('secondary', 0)} !important;
  text-decoration: underline;
`;

const TapToCopyCellWrapper = WithDirection(TapToCopyCellWrapperStyles);
const DropdownWrapper = WithDirection(DropdownWrapperStyles);

export { TapToCopyCellWrapper, DropdownWrapper };
